import { useEffect, useMemo, useState } from 'react';

// third party
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

// zercode packages
import { userListSchema, type UserSummary } from '@zercode/api-client';
import { FILTER_DEBOUNCE_MS } from '@zercode/forms';

// project imports
import { MAX_IDS_PER_QUERY } from '../../lib/idQuery';
import { identityClient } from '../../lib/zercode';

// ==============================|| USR-COL — SERVER-SEARCHED USER PICKER ||============================== //
//
// The people half of what `schoolSearch.ts` does for schools, and it exists for the same measured reason.
// Every "pick a person" field in this app fetches `?page=1&pageSize=100` and filters THAT in the browser
// (`UserDetail`'s teacher / parent / salesperson pickers say so in a comment). 100 is the platform page
// ceiling [STD-API-8], so a bigger number is a 400, not more rows — and the demo tenant already holds 127
// users. A picker that cannot see the 101st person is not a picker, it is a list of the first hundred.
//
// ⚠ THE SERVER SIDE OF THIS ALREADY EXISTED. `ListUsersQuery` has taken `?name=` (ILIKE over the name
// PARTS and the composed full name) and `?ids=a,b,c` (ORD-LIST) for a long time; nothing sent them from a
// picker. mig 399 indexes the four name columns with pg_trgm, so the search is index-served.
//
// ⚠ `?roleType=` IS THE SCOPE, AND IT IS NOT DECORATION. The teacher filter must offer people who can
// actually BE a teacher: `TeacherReference` refuses a teacher_user_id whose holder does not carry a live
// supervisor-typed role, on BOTH write paths (self and admin). Offering everyone would list 127 people of
// whom 7 are selectable, and the filter would return nothing for the other 120 — which reads as a broken
// filter rather than as "that person is not a teacher".

/** Rows per request. Small for the same reason the school picker's is: a picker is read by eye. */
export const USER_PAGE_SIZE = 15;

/**
 * Below this many characters the term is IGNORED and the first page is served in e-mail order.
 *
 * ⚠ Same rule as the school picker, same cause: `ILIKE '%a%'` cannot use the trigram index, so a
 * one-character term is a sequential scan of identity.users on every keystroke.
 */
export const MIN_SEARCH_LENGTH = 2;

/**
 * Ids resolved per request. The endpoint accepts 100 (`ListUsersQueryValidator.MaxIds`), but a 100-id query
 * string is refused in production before it reaches the app — see lib/idQuery.ts.
 */
const IDS_PER_REQUEST = MAX_IDS_PER_QUERY;

/** Hard stop on the id-resolution loop (200 ids); a page of rows cannot name more people than this. */
const MAX_ID_REQUESTS = 5;

/** The catalogue row a person-picker needs: the option, and the handle that disambiguates it. */
export interface UserRow {
  id: string;
  displayName: string;
  email?: string | null;
  username?: string | null;
  /** USER-CODE (mig 629): shown under the name in a picker — the one identifier two same-named people never share. */
  userCode?: string | null;
  /** OFR-ADM: the employer (`users.company_id`) — the partner-user picker names it under each person. */
  companyId?: string | null;
}

export interface UserSearchResult {
  rows: UserRow[];
  totalItems: number;
  isLoading: boolean;
  /** The user has typed something that is not being searched yet (1 char, or mid-debounce). */
  isTyping: boolean;
  /**
   * There is nobody in this SCOPE at all — distinct from "no match for what you typed".
   *
   * ⚠ Answered from the UNFILTERED page, never from the current term, so it does not flicker on every
   * keystroke. Read it through `useUserScopeEmpty`, which keeps the empty-term answer settled.
   */
  isEmptyScope: boolean;
  hasMore: boolean;
  loadMore: () => void;
}

const toRow = (u: UserSummary): UserRow => ({
  id: u.id,
  displayName: u.displayName,
  email: u.email,
  username: u.username,
  userCode: u.userCode,
  companyId: u.companyId
});

/**
 * ⚠ THE LABEL FALLS BACK THROUGH THE HANDLE, NEVER TO EMPTY. STU-HANDLE (mig 314) made `email` nullable
 * and `displayName` can be a bare e-mail for an invited user who has not filled a profile in — an
 * unlabelled row is an unpickable row.
 */
export function userOptionLabel(row: UserRow): string {
  return row.displayName || row.email || row.username || row.id;
}

/** The debounced, paged user search behind a picker. Optionally scoped to one role type. */
export function useUserSearch(
  term: string,
  roleType?: string,
  isSalesperson?: boolean,
  /**
   * ⚠ ONLY `useUserScopeEmpty` PASSES FALSE, and it exists to stop a probe nobody asked for. That hook
   * runs inside EVERY picker (hooks cannot be conditional), so without this the teacher and
   * Teeneagle-parent fields would each fire an extra unscoped page they never read. A disabled
   * react-query stays `isPending` forever, which lands on `isEmptyScope: false` — the safe answer.
   */
  enabled = true,
  /**
   * OFR-ADM: narrow to holders of ONE role CODE (`partner_employee` for the offer buyer). Not a role
   * TYPE: the offer route checks the code, and a partner-typed tenant role with another code cannot buy.
   */
  roleCode?: string,
  /**
   * APPT-HOST: narrow to holders of one PERMISSION code (`engagement.appointments.confirm` for the appointment host
   * picker) — Identity's `?permission=` (UserFilters.ByPermission).
   */
  permission?: string
): UserSearchResult {
  const trimmed = term.trim();
  const [settled, setSettled] = useState(trimmed);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(trimmed), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmed]);

  const effective = settled.length >= MIN_SEARCH_LENGTH ? settled : '';

  const query = useInfiniteQuery({
    // ⚠ BOTH SCOPES ARE IN THE KEY. A scoped and an unscoped search for the same term would otherwise
    // share one cache entry, and whichever ran first would supply the other's rows — a wrong list that
    // looks exactly like a working one.
    queryKey: ['identity-user-search', roleType ?? '', isSalesperson ? 'sales' : '', roleCode ?? '', permission ?? '', effective],
    initialPageParam: 1,
    enabled,
    staleTime: 60 * 1000,
    queryFn: async ({ pageParam }) => {
      const { data, meta } = await identityClient.get<unknown>('/api/v1/users', {
        query: {
          page: pageParam,
          pageSize: USER_PAGE_SIZE,
          name: effective || undefined,
          roleType,
          // SP-ROLE: sent only when TRUE. `false` is not the complement — the server treats it as no
          // filter — so sending it would be a parameter that reads as a narrowing and is not one.
          isSalesperson: isSalesperson ? true : undefined,
          roleCode,
          permission
        }
      });
      const parsed = userListSchema.parse(data);
      // ⚠ The total lives in `meta.pagination` [STD-API-2]; `pagedListSchema` reports the PAGE's own
      // length, so reading it would say "15 / 15" on every full page and hide the "load more" row.
      return { items: parsed.items, totalItems: meta.pagination?.totalItems ?? parsed.items.length };
    },
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, p) => n + p.items.length, 0);
      return loaded < last.totalItems ? all.length + 1 : undefined;
    }
  });

  const rows = useMemo<UserRow[]>(
    () => (query.data?.pages ?? []).flatMap((p) => p.items.map(toRow)),
    [query.data]
  );

  return {
    rows,
    totalItems: query.data?.pages[0]?.totalItems ?? 0,
    // Only meaningful on the UNFILTERED read — a caller that has typed something asks
    // `useUserScopeEmpty`, which keeps its own settled answer for the empty term.
    isEmptyScope: settled === '' && !query.isPending && (query.data?.pages[0]?.totalItems ?? 0) === 0,
    // `isFetching`, not `isPending`: page 2 of the same term must show the spinner too.
    isLoading: query.isFetching,
    isTyping: trimmed !== settled,
    hasMore: query.hasNextPage,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    }
  };
}

/**
 * SP-ROLE: "is this scope empty for everyone, or did I just mistype?" — the sentence a salesperson
 * picker renders instead of an empty listbox.
 *
 * ⚠ THE TWO READ IDENTICALLY AND MEAN OPPOSITE THINGS. "No role is marked as a salesperson yet" is an
 * instruction to go and tick a box in Roles; "nothing matches ABC" is an instruction to type less. With
 * no backfill (mig 501) the first state is what EVERY tenant sees until it configures the feature, so
 * an unexplained empty box would be most admins' first impression of it.
 *
 * ⚠ IT COSTS NOTHING EXTRA. It calls the search with an EMPTY term, so its react-query key is the one
 * the picker itself uses before anything is typed: one request, shared. Once the admin starts typing the
 * picker moves to a new key and this keeps reading the cached unfiltered page — which is the point,
 * because the sentence must not appear and vanish as a term is typed.
 *
 * ⚠ `isLoading` IS NOT COSMETIC. An empty list mid-fetch is "not yet"; rendering that as "there is
 * none" sends an admin to configure something that is already correct. Callers wait.
 *
 * Copied in shape from `usePartnerScopeEmpty`, which solved exactly this for partners.
 */
export function useUserScopeEmpty(isSalesperson?: boolean): { isEmpty: boolean; isLoading: boolean } {
  // Enabled ONLY for a scoped picker: an unscoped one has no scope to be empty, and probing it would be
  // a second request per field for an answer nobody reads. See `useUserSearch`'s `enabled`.
  const scope = useUserSearch('', undefined, isSalesperson, Boolean(isSalesperson));
  return { isEmpty: scope.isEmptyScope, isLoading: scope.isLoading };
}

export interface UserNamesResult {
  /** id → display label, for the ids that resolved. A missing id means deleted, or past the cap. */
  nameById: Map<string, string>;
  isLoading: boolean;
}

/**
 * Resolve a KNOWN SET of user ids to names, in one request per 100.
 *
 * ⚠ THE OTHER HALF OF A SEARCHABLE PICKER, and the half that is easy to forget. A saved row carries a
 * bare guid (`user_profiles.teacher_user_id`), and a picker holding one page of 15 will not have it — so
 * without this a chosen teacher renders as a guid, and so does every Teacher cell on the list.
 *
 * ⚠ `ids` GOES OVER THE WIRE AS ONE COMMA-JOINED STRING. Handing the client an ARRAY makes it append a
 * repeated `ids=` per item, which the endpoint's single `string? ids` parameter does not read.
 */
export function useUserNames(ids: readonly string[]): UserNamesResult {
  const wanted = useMemo(() => [...new Set(ids.filter(Boolean))].sort(), [ids]);

  const query = useQuery({
    queryKey: ['identity-user-names', wanted],
    enabled: wanted.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const rows: UserRow[] = [];
      for (let i = 0; i < wanted.length && i < IDS_PER_REQUEST * MAX_ID_REQUESTS; i += IDS_PER_REQUEST) {
        const chunk = wanted.slice(i, i + IDS_PER_REQUEST);
        const { data } = await identityClient.get<unknown>('/api/v1/users', {
          query: { page: 1, pageSize: IDS_PER_REQUEST, ids: chunk.join(',') }
        });
        rows.push(...userListSchema.parse(data).items.map(toRow));
      }
      return rows;
    }
  });

  return useMemo(
    () => ({
      nameById: new Map((query.data ?? []).map((r) => [r.id, userOptionLabel(r)])),
      isLoading: query.isLoading
    }),
    [query.data, query.isLoading]
  );
}

/**
 * Ids resolved by {@link useUserSummaries} — 50 requests.
 *
 * ⚠ ITS OWN CEILING, NOT `MAX_ID_REQUESTS`. That one is 2, and correctly so: it serves a PICKER and a
 * list PAGE, which cannot name more than a couple of hundred people. This one serves a whole event
 * roster, so it is sized to `ROSTER_MAX_ROWS` (5,000) and the caller that feeds it reports the same
 * truncation the roster hook does. Sharing one constant would have silently cut the roster at 200.
 */
const MAX_SUMMARY_ID_REQUESTS = 125; // 125 × 40 = ROSTER_MAX_ROWS (5,000)

export interface UserSummariesResult {
  /** id → the full row, for the ids that resolved. A missing id means deleted, or past the cap. */
  byId: Map<string, UserSummary>;
  isLoading: boolean;
  /**
   * ⚠ WORTH RENDERING, NOT SWALLOWING. The usual cause is a 403: the caller holds the list's own
   * permission but not `identity.users.view`, so every column fed from here is blank. A blank cell reads
   * as "this person has no school"; only the caller can say "we were not allowed to look".
   */
  isError: boolean;
  /** More ids were handed in than {@link MAX_SUMMARY_ID_REQUESTS} covers; `byId` is a prefix. */
  truncated: boolean;
}

/**
 * Resolve a KNOWN SET of user ids to their WHOLE row, in one request per 100.
 *
 * ⚠ THE DIFFERENCE FROM `useUserNames` IS THE PAYLOAD, NOT THE ENDPOINT. That one keeps a name cache for
 * pickers; this one hands back `UserSummary` itself, because the caller needs the five reference fields
 * the list projection carries — `roles`, `countryDefinitionId`, `schoolId`, `teacherUserId` and
 * `ageCategories`. Those are IDS, not names: the country and the age bands belong to the Definitions
 * service, which Identity may not read [STD-OWN], so resolving them is still the client's job (`geo`,
 * `useAgeCategories`, `useSchoolNames`, `useUserNames`).
 *
 * ⚠ THE FIELDS ARE ONLY ON THE LIST PROJECTION. The same DTO comes back from six COMMANDS with all five
 * absent — `userSummarySchema` marks them nullish for exactly that reason. So this must stay on
 * `GET /api/v1/users`; reading a user through any write's ACK would parse fine and answer null to
 * everything.
 *
 * ⚠ `ids` GOES OVER THE WIRE AS ONE COMMA-JOINED STRING, per batch. An array makes the client append a
 * repeated `ids=` that the endpoint's single `string? ids` parameter never reads — the request would 200
 * with the first 100 users of the tenant, which is the bug this hook exists to end.
 */
export function useUserSummaries(ids: readonly string[]): UserSummariesResult {
  const wanted = useMemo(() => [...new Set(ids.filter(Boolean))].sort(), [ids]);
  const capacity = IDS_PER_REQUEST * MAX_SUMMARY_ID_REQUESTS;

  // One string rather than the array itself: react-query hashes the key on every render, and a roster's
  // worth of guids is the kind of thing that should be hashed once per CHANGE.
  const idsKey = useMemo(() => wanted.join(','), [wanted]);

  const query = useQuery({
    queryKey: ['identity-user-summaries', idsKey],
    enabled: wanted.length > 0,
    // A 403 is an answer, not a blip. Retrying it three times delays the honest "we could not look" by a
    // couple of seconds and changes nothing.
    retry: false,
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ signal }) => {
      const rows: UserSummary[] = [];
      for (let i = 0; i < wanted.length && i < capacity; i += IDS_PER_REQUEST) {
        const chunk = wanted.slice(i, i + IDS_PER_REQUEST);
        const { data } = await identityClient.get<unknown>('/api/v1/users', {
          signal,
          query: { page: 1, pageSize: IDS_PER_REQUEST, ids: chunk.join(',') }
        });
        rows.push(...userListSchema.parse(data).items);
      }
      return rows;
    }
  });

  return useMemo(
    () => ({
      byId: new Map((query.data ?? []).map((row) => [row.id, row])),
      isLoading: query.isLoading,
      isError: query.isError,
      truncated: wanted.length > capacity
    }),
    [query.data, query.isLoading, query.isError, wanted.length, capacity]
  );
}

/** The footer row's option id. Not an id anything can hold — a guid never collides with it. */
export const USER_MORE_ROW = '__user-pick-more__';

export interface UserPickerModel {
  input: string;
  setInput: (next: string) => void;
  search: UserSearchResult;
  nameById: Map<string, string>;
  /**
   * USER-MERGE: "TE2701479 · saif@x.test" — the line under each option. Two people called "Saif Test" and "Saif test"
   * slip past the duplicate-name rule above (it compares exact labels), and a merge picked by name alone is an
   * irreversible guess. Optional so a test double of this model need not invent it.
   */
  detailById?: Map<string, string>;
  options: string[];
  footer: string | null;
}

/**
 * The debounced search, the label cache, the option list and the footer — the same model
 * `useSchoolPicker` provides, for people.
 */
export function useUserPicker(
  selected: readonly string[],
  options?: { roleType?: string; roleCode?: string; isSalesperson?: boolean; extraNames?: Map<string, string>; permission?: string }
): UserPickerModel {
  const intl = useIntl();
  const [input, setInput] = useState('');
  const search = useUserSearch(input, options?.roleType, options?.isSalesperson, true, options?.roleCode, options?.permission);

  // ⚠ LABELS MUST OUTLIVE THE SEARCH TERM. A label is read through `getOptionLabel`, and the row it came
  // from is gone the moment the term changes — so a picker reading labels only from the current page
  // would turn the closed field's own text into a guid on the next keystroke. This accumulates instead.
  const [labelCache] = useState(() => new Map<string, string>());
  const nameById = useMemo(() => {
    // Both sources OVERWRITE, freshest last — the school picker's note explains why filling only a
    // missing entry is the wrong rule (the first label an id gets is the worst one).
    for (const [id, name] of options?.extraNames ?? []) labelCache.set(id, name);
    // Display names repeat (two real "Ada Lovelace"s on the demo tenant): a name shared by two rows of the
    // page gets the person's handle appended, so the rows differ by something the admin can act on — the
    // rule `toUserOptions` applies to a fixed list, applied here to the server-searched page.
    const counts = new Map<string, number>();
    for (const row of search.rows) counts.set(userOptionLabel(row), (counts.get(userOptionLabel(row)) ?? 0) + 1);
    for (const row of search.rows) {
      const label = userOptionLabel(row);
      const handle = row.email || row.username || row.id;
      labelCache.set(row.id, (counts.get(label) ?? 0) > 1 && handle !== label ? `${label} (${handle})` : label);
    }
    return new Map(labelCache);
  }, [search.rows, options?.extraNames, labelCache]);

  const [detailCache] = useState(() => new Map<string, string>());
  const detailById = useMemo(() => {
    for (const row of search.rows) {
      const detail = [row.userCode, row.email || row.username].filter(Boolean).join(' · ');
      if (detail) detailCache.set(row.id, detail);
    }
    return new Map(detailCache);
  }, [search.rows, detailCache]);

  const tooShort = input.trim().length > 0 && input.trim().length < MIN_SEARCH_LENGTH;

  /**
   * SP-ROLE: is this scope empty for everyone? Kept on the settled EMPTY-term answer so the sentence
   * below cannot appear and vanish as a term is typed. `isLoading` is honoured: an empty list mid-fetch
   * is "not yet", and rendering that as "there is none" sends an admin to fix something already correct.
   */
  const scope = useUserScopeEmpty(options?.isSalesperson);
  const scopeUnset = Boolean(options?.isSalesperson) && !scope.isLoading && scope.isEmpty;

  /**
   * ⚠ THE TOO-SHORT CASE IS THE ONE THAT MISLEADS: below `MIN_SEARCH_LENGTH` the term is not sent, so the
   * listbox still holds the first page — rows that plainly do not match what was typed. Said out loud as
   * a LISTBOX ROW, because there ARE options in that state and `noOptionsText` never fires.
   *
   * ⚠ THE UNCONFIGURED-SCOPE SENTENCE OUTRANKS BOTH, and it is a LISTBOX ROW for the same reason —
   * `noOptionsText` would not fire in the case that matters most. A user who already HAS a salesperson
   * saved sees that one person in the list (the picker always keeps the selection visible), so the
   * listbox is not empty, and without this row the admin would be shown one name, no others, and no
   * explanation. That person is precisely the one who fails the new rule.
   */
  const footer = scopeUnset
    ? intl.formatMessage({
        id: 'user-pick-no-sales-role',
        defaultMessage: 'No role is marked as a salesperson yet — tick one in Roles.'
      })
    : tooShort
    ? intl.formatMessage(
        { id: 'user-pick-min', defaultMessage: 'Aramak için en az {min} karakter yazın' },
        { min: MIN_SEARCH_LENGTH }
      )
    : search.totalItems > search.rows.length
      ? intl.formatMessage(
          {
            id: 'user-pick-more',
            defaultMessage: '{shown} / {total} kişi — sonraki {size} için tıklayın veya yazarak daraltın'
          },
          { shown: search.rows.length, total: search.totalItems, size: USER_PAGE_SIZE }
        )
      : null;

  // The selection comes first so a chosen person is always in the listbox even when the current term
  // does not match them; the server page follows, then the footer.
  const pickerOptions = useMemo(() => {
    const fromSearch = search.rows.map((r) => r.id).filter((id) => !selected.includes(id));
    const rows = [...selected.filter((id) => id !== ''), ...fromSearch];
    return footer ? [...rows, USER_MORE_ROW] : rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.rows, footer, selected.join(',')]);

  return { input, setInput, search, nameById, detailById, options: pickerOptions, footer };
}
